import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField
} from '@mui/material';
import { apiFetch } from './api';
import { useTournamentList } from './TournamentListContext';
import CategoryChip from './CategoryChip';

// Helper function to get year from birthday
const getYearOfBirth = (birthday) => {
  if (!birthday) return '';
  const date = new Date(birthday);
  return date.getFullYear();
};

// Helper function to check if a player is eligible for a category
// Based on the player's kat field from the database
const isEligibleForCategory = (playerKat, selectedCategory) => {
  if (!selectedCategory || selectedCategory === 'Alle') return true;
  if (!playerKat) return false;
  
  // Extract number from category (e.g., "U10" -> 10)
  const playerAge = parseInt(playerKat.substring(1));
  const selectedAge = parseInt(selectedCategory.substring(1));
  
  // Player is eligible if their category is equal to or younger than the selected category
  return playerAge <= selectedAge;
};

// Export combined results to CSV
const exportSeriesResults = (combinedData, metaTournaments, seriesName) => {
  if (!combinedData || combinedData.length === 0) {
    alert('Keine Ergebnisse zum Exportieren vorhanden.');
    return;
  }

  // Create header row with meta-tournament locations
  const metaTournamentLocations = metaTournaments.map(mt => mt.location);
  const header = ['Name', 'Jahrgang', 'Kat', 'Verein', ...metaTournamentLocations, 'Ges', 'Wtg'];

  const rows = combinedData.map(player => {
    const name = `${player.first_name} ${player.last_name}`;
    const yearOfBirth = getYearOfBirth(player.birthday);
    const category = player.kat || '';
    const club = player.club || '';
    
    // Get points for each meta-tournament
    const metaTournamentPoints = metaTournaments.map(metaTournament => {
      const result = player.metaTournamentResults[metaTournament.id];
      const points = result ? result.points || 0 : 0;
      return points.toFixed(1).replace('.', ','); // Use comma as decimal separator
    });
    
    const total = player.totalPoints.toFixed(1).replace('.', ',');
    const dropScore = player.totalPointsWithDrop.toFixed(1).replace('.', ',');
    
    return [name, yearOfBirth, category, club, ...metaTournamentPoints, total, dropScore];
  });

  // Confirm export
  const confirmed = confirm(
    `${combinedData.length} Spieler aus ${metaTournaments.length} Turnieren exportieren?`
  );
  if (!confirmed) return;

  // Create CSV content with semicolon separator (European standard for comma decimal separator)
  const csvContent = [header, ...rows]
    .map(row => row.map(field => {
      const fieldStr = String(field);
      return /[;"\n]/.test(fieldStr) ? `"${fieldStr.replace(/"/g, '""')}"` : fieldStr;
    }).join(';'))
    .join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${seriesName || 'tournament_series'}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Export club rankings to CSV
const exportClubRankings = (combinedData, seriesName) => {
  if (!combinedData || combinedData.length === 0) {
    alert('Keine Ergebnisse zum Exportieren vorhanden.');
    return;
  }

  // Group players by club
  const clubMap = new Map();
  
  combinedData.forEach(player => {
    const club = player.club || 'Ohne Verein';
    if (!clubMap.has(club)) {
      clubMap.set(club, []);
    }
    clubMap.get(club).push(player);
  });

  // Calculate club rankings (best 3 players per club)
  const clubRankings = [];
  
  clubMap.forEach((players, club) => {
    // Sort players by drop score (descending)
    const sortedPlayers = [...players].sort((a, b) => b.totalPointsWithDrop - a.totalPointsWithDrop);
    
    // Take best 3 players
    const best3 = sortedPlayers.slice(0, 3);
    
    // Calculate total
    const clubTotal = best3.reduce((sum, player) => sum + player.totalPointsWithDrop, 0);
    
    clubRankings.push({
      club,
      players: best3,
      total: clubTotal,
      playerCount: players.length
    });
  });

  // Sort clubs by total points (descending)
  clubRankings.sort((a, b) => b.total - a.total);

  // Create CSV
  const header = ['Rang', 'Verein', 'Spieler 1', 'Punkte 1', 'Spieler 2', 'Punkte 2', 'Spieler 3', 'Punkte 3', 'Ges', 'Spieler gesamt'];
  
  const rows = clubRankings.map((clubRanking, index) => {
    const rank = index + 1;
    const club = clubRanking.club;
    
    const player1Name = clubRanking.players[0] ? `${clubRanking.players[0].first_name} ${clubRanking.players[0].last_name}` : '';
    const player1Points = clubRanking.players[0] ? clubRanking.players[0].totalPointsWithDrop.toFixed(1).replace('.', ',') : '';
    
    const player2Name = clubRanking.players[1] ? `${clubRanking.players[1].first_name} ${clubRanking.players[1].last_name}` : '';
    const player2Points = clubRanking.players[1] ? clubRanking.players[1].totalPointsWithDrop.toFixed(1).replace('.', ',') : '';
    
    const player3Name = clubRanking.players[2] ? `${clubRanking.players[2].first_name} ${clubRanking.players[2].last_name}` : '';
    const player3Points = clubRanking.players[2] ? clubRanking.players[2].totalPointsWithDrop.toFixed(1).replace('.', ',') : '';
    
    const total = clubRanking.total.toFixed(1).replace('.', ',');
    const playerCount = clubRanking.playerCount;
    
    return [rank, club, player1Name, player1Points, player2Name, player2Points, player3Name, player3Points, total, playerCount];
  });

  // Confirm export
  const confirmed = confirm(
    `${clubRankings.length} Vereine exportieren?`
  );
  if (!confirmed) return;

  // Create CSV content
  const csvContent = [header, ...rows]
    .map(row => row.map(field => {
      const fieldStr = String(field);
      return /[;"\n]/.test(fieldStr) ? `"${fieldStr.replace(/"/g, '""')}"` : fieldStr;
    }).join(';'))
    .join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${seriesName || 'tournament_series'}_vereinswertung.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function TournamentSeries() {
  const { allTournaments } = useTournamentList();
  const [selectedTournamentIds, setSelectedTournamentIds] = useState([]);
  const [combinedData, setCombinedData] = useState(null);
  const [metaTournaments, setMetaTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [seriesName, setSeriesName] = useState('');
  const [ageCategory, setAgeCategory] = useState('Alle');

  // Sort tournaments by date (newest first)
  const sortedTournaments = useMemo(() => {
    if (!allTournaments || !Array.isArray(allTournaments)) {
      return [];
    }
    return [...allTournaments].sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [allTournaments]);

  const handleTournamentToggle = (tournamentId) => {
    setSelectedTournamentIds(prev => {
      if (prev.includes(tournamentId)) {
        return prev.filter(id => id !== tournamentId);
      } else {
        return [...prev, tournamentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTournamentIds.length === sortedTournaments.length) {
      setSelectedTournamentIds([]);
    } else {
      setSelectedTournamentIds(sortedTournaments.map(t => t.id));
    }
  };

  const handleCompileSeries = async () => {
    if (selectedTournamentIds.length === 0) {
      alert('Bitte wählen Sie mindestens ein Turnier aus.');
      return;
    }

    setLoading(true);
    setError('');
    setCombinedData(null);

    try {
      // Group tournaments by date and location (meta-tournaments)
      const metaTournamentMap = new Map();
      const tournamentToMetaMap = new Map(); // Maps tournament ID to meta tournament ID
      
      selectedTournaments.forEach(tournament => {
        const date = tournament.date ? new Date(tournament.date).toISOString().split('T')[0] : 'unknown';
        const location = (tournament.location || 'Unbekannt').trim();
        const metaKey = `${date}_${location}`;
        
        if (!metaTournamentMap.has(metaKey)) {
          metaTournamentMap.set(metaKey, {
            id: metaKey,
            location: location,
            date: date,
            tournamentIds: [],
            rounds: tournament.rounds || 5 // Use first tournament's rounds
          });
        }
        
        const metaTournament = metaTournamentMap.get(metaKey);
        metaTournament.tournamentIds.push(tournament.id);
        tournamentToMetaMap.set(tournament.id, metaKey);
      });

      // Sort meta-tournaments by date (oldest first, newest last)
      const metaTournaments = Array.from(metaTournamentMap.values()).sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });

      // Fetch players from all selected tournaments
      const tournamentPlayersPromises = selectedTournamentIds.map(tournamentId =>
        apiFetch(`/tournaments/${tournamentId}/players`)
      );

      const allTournamentPlayers = await Promise.all(tournamentPlayersPromises);

      // Create a map of tournament rounds for normalization
      const tournamentRoundsMap = new Map();
      selectedTournaments.forEach(tournament => {
        // Use 5 as default if rounds is not set
        tournamentRoundsMap.set(tournament.id, tournament.rounds || 5);
      });

      // Helper function to normalize points to 5 rounds
      const normalizePoints = (points, tournamentId) => {
        const rounds = tournamentRoundsMap.get(tournamentId) || 5;
        const normalized = points * (5 / rounds);
        return Math.round(normalized * 10) / 10; // Round to 1 decimal place
      };

      // Create a map of players with their results across meta-tournaments
      const playerMap = new Map();

      selectedTournamentIds.forEach((tournamentId, index) => {
        const tournamentPlayers = Array.isArray(allTournamentPlayers[index])
          ? allTournamentPlayers[index]
          : [];

        const metaTournamentId = tournamentToMetaMap.get(tournamentId);

        tournamentPlayers.forEach(tp => {
          // Skip entries without player data
          if (!tp.player_id || !tp.player) return;

          const playerId = tp.player_id;
          
          if (!playerMap.has(playerId)) {
            playerMap.set(playerId, {
              player_id: playerId,
              first_name: tp.player.first_name,
              last_name: tp.player.last_name,
              birthday: tp.player.birthday,
              kat: tp.player.kat,
              club: tp.player.club,
              metaTournamentResults: {}, // Results grouped by meta-tournament
              totalPoints: 0
            });
          }

          const player = playerMap.get(playerId);
          const rawPoints = tp.points || 0;
          const normalizedPoints = normalizePoints(rawPoints, tournamentId);
          
          // Store result in meta-tournament, keeping the best score if player plays multiple tournaments in same location/date
          if (!player.metaTournamentResults[metaTournamentId]) {
            player.metaTournamentResults[metaTournamentId] = {
              points: normalizedPoints,
              rawPoints: rawPoints,
              rank: tp.rank || tp.ranking
            };
          } else {
            // If player already has a result for this meta-tournament, keep the better one
            if (normalizedPoints > player.metaTournamentResults[metaTournamentId].points) {
              player.metaTournamentResults[metaTournamentId] = {
                points: normalizedPoints,
                rawPoints: rawPoints,
                rank: tp.rank || tp.ranking
              };
            }
          }
        });
      });

      // Calculate total points based on meta-tournaments
      playerMap.forEach((player) => {
        player.totalPoints = Object.values(player.metaTournamentResults)
          .reduce((sum, result) => sum + result.points, 0);
      });

      // Calculate drop score (total minus lowest score) for each player
      playerMap.forEach((player) => {
        // Get scores for ALL meta-tournaments (0 if not played)
        const allScores = metaTournaments.map(metaTournament => {
          const result = player.metaTournamentResults[metaTournament.id];
          return result ? result.points : 0;
        });
        
        if (allScores.length > 1) {
          // Sort scores to find the lowest
          const sortedScores = [...allScores].sort((a, b) => a - b);
          const lowestScore = sortedScores[0];
          // Total minus the lowest score
          player.totalPointsWithDrop = player.totalPoints - lowestScore;
        } else {
          // If only 1 meta-tournament, no drop possible
          player.totalPointsWithDrop = player.totalPoints;
        }
      });

      // Convert map to array and sort by drop score (descending), then by total points on tie
      let combinedArray = Array.from(playerMap.values()).sort((a, b) => {
        // First compare by drop score
        const dropDiff = b.totalPointsWithDrop - a.totalPointsWithDrop;
        if (dropDiff !== 0) {
          return dropDiff;
        }
        // If drop scores are equal, compare by total points
        return b.totalPoints - a.totalPoints;
      });

      // Filter by age category if selected
      if (ageCategory && ageCategory !== 'Alle') {
        combinedArray = combinedArray.filter(player => 
          isEligibleForCategory(player.kat, ageCategory)
        );
      }

      setCombinedData(combinedArray);
      setMetaTournaments(metaTournaments);
    } catch (err) {
      console.error('Error compiling series:', err);
      setError(`Fehler beim Zusammenstellen der Serie: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    exportSeriesResults(combinedData, metaTournaments, seriesName || 'turnierserie');
  };

  const handleExportClubs = () => {
    exportClubRankings(combinedData, seriesName || 'turnierserie');
  };

  const handlePrint = () => {
    window.print();
  };

  const selectedTournaments = useMemo(() => {
    if (!sortedTournaments || !Array.isArray(sortedTournaments)) {
      return [];
    }
    return sortedTournaments.filter(t => selectedTournamentIds.includes(t.id));
  }, [sortedTournaments, selectedTournamentIds]);

  // Calculate club rankings
  const clubRankings = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return [];
    }

    // Group players by club
    const clubMap = new Map();
    
    combinedData.forEach(player => {
      const club = player.club || 'Ohne Verein';
      if (!clubMap.has(club)) {
        clubMap.set(club, []);
      }
      clubMap.get(club).push(player);
    });

    // Calculate club rankings (best 3 players per club)
    const rankings = [];
    
    clubMap.forEach((players, club) => {
      // Sort players by drop score (descending)
      const sortedPlayers = [...players].sort((a, b) => b.totalPointsWithDrop - a.totalPointsWithDrop);
      
      // Take best 3 players
      const best3 = sortedPlayers.slice(0, 3);
      
      // Calculate total
      const clubTotal = best3.reduce((sum, player) => sum + player.totalPointsWithDrop, 0);
      
      rankings.push({
        club,
        players: best3,
        total: clubTotal,
        playerCount: players.length
      });
    });

    // Sort clubs by total points (descending)
    rankings.sort((a, b) => b.total - a.total);

    return rankings;
  }, [combinedData]);

  return (
    <Box sx={{ p: 2 }}>
      <style>
        {`
          @media print {
            /* Hide buttons and selection UI when printing */
            .no-print {
              display: none !important;
            }
            
            /* Adjust layout for printing */
            body {
              background: white;
            }
            
            /* Remove scrollbars and show all content */
            .MuiTableContainer-root {
              max-height: none !important;
              overflow: visible !important;
            }
            
            .MuiBox-root {
              max-height: none !important;
              overflow: visible !important;
            }
            
            /* Ensure tables fit on page */
            table {
              page-break-inside: auto;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            thead {
              display: table-header-group;
            }
            
            /* Add series name as header */
            .print-header {
              display: block !important;
              text-align: center;
              font-size: 18pt;
              font-weight: bold;
              margin-bottom: 20px;
            }
            
            /* Optimize spacing for print */
            .MuiCard-root {
              box-shadow: none !important;
              border: 1px solid #ddd;
              margin-bottom: 30px;
              page-break-after: auto;
            }
            
            .MuiCardContent-root {
              padding: 8px !important;
            }
            
            /* Make sure tables are readable */
            .MuiTableCell-root {
              font-size: 9pt;
              padding: 3px !important;
            }
            
            /* Preserve background colors */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Remove page margins to maximize space */
            @page {
              margin: 0.5cm;
            }
          }
          
          /* Hide print header on screen */
          .print-header {
            display: none;
          }
        `}
      </style>
      
      <div className="print-header">
        {seriesName || 'Turnierserie'}
      </div>
      
      <Typography variant="h5" sx={{ mb: 3 }} className="no-print">
        Turnierserie zusammenstellen
      </Typography>

      <Card sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Turniere auswählen
          </Typography>
          
          <TextField
            label="Serienname (für Export)"
            value={seriesName}
            onChange={(e) => setSeriesName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="z.B. Jugend Grand Prix 2025"
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="age-category-label">Alterskategorie filtern</InputLabel>
            <Select
              labelId="age-category-label"
              value={ageCategory}
              label="Alterskategorie filtern"
              onChange={(e) => setAgeCategory(e.target.value)}
            >
              <MenuItem value="Alle">Alle Spieler</MenuItem>
              <MenuItem value="U8">U8</MenuItem>
              <MenuItem value="U10">U10</MenuItem>
              <MenuItem value="U12">U12</MenuItem>
              <MenuItem value="U14">U14</MenuItem>
              <MenuItem value="U16">U16</MenuItem>
              <MenuItem value="U18">U18</MenuItem>
              <MenuItem value="U20">U20</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Button 
              onClick={handleSelectAll} 
              size="small"
              variant="outlined"
            >
              {selectedTournamentIds.length === sortedTournaments.length
                ? 'Alle abwählen'
                : 'Alle auswählen'}
            </Button>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {selectedTournamentIds.length} von {sortedTournaments.length} Turnieren ausgewählt
            </Typography>
          </Box>

          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {sortedTournaments.map(tournament => {
              const dateStr = tournament.date
                ? new Date(tournament.date).toLocaleDateString('de-DE')
                : '';
              return (
                <FormControlLabel
                  key={tournament.id}
                  control={
                    <Checkbox
                      checked={selectedTournamentIds.includes(tournament.id)}
                      onChange={() => handleTournamentToggle(tournament.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {tournament.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[tournament.location, dateStr].filter(Boolean).join(' • ')}
                      </Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', width: '100%', mb: 1 }}
                />
              );
            })}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCompileSeries}
              disabled={selectedTournamentIds.length === 0 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Serie zusammenstellen'}
            </Button>
            {combinedData && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleExport}
                >
                  Spieler exportieren (CSV)
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleExportClubs}
                >
                  Vereinswertung exportieren (CSV)
                </Button>
                <Button
                  variant="outlined"
                  onClick={handlePrint}
                >
                  Drucken
                </Button>
              </>
            )}
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </CardContent>
      </Card>

      {combinedData && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Einzelwertung ({combinedData.length} Spieler, {metaTournaments.length} Turniere)
              </Typography>

              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Rang
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Jahrgang
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Kategorie
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Verein
                      </TableCell>
                      {metaTournaments.map(metaTournament => (
                        <TableCell
                          key={metaTournament.id}
                          align="right"
                          sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 80 }}
                          title={`${metaTournament.location} (${new Date(metaTournament.date).toLocaleDateString('de-DE')})`}
                        >
                          {metaTournament.location}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}
                      >
                        Ges
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }}
                      >
                        Wtg
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combinedData.map((player, index) => (
                      <TableRow key={player.player_id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {player.first_name} {player.last_name}
                            {player.kat && (
                              <CategoryChip 
                                player={player} 
                                size="small" 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{getYearOfBirth(player.birthday)}</TableCell>
                        <TableCell>{player.kat || '-'}</TableCell>
                        <TableCell>{player.club || '-'}</TableCell>
                        {metaTournaments.map(metaTournament => {
                          const result = player.metaTournamentResults[metaTournament.id];
                          return (
                            <TableCell key={metaTournament.id} align="right">
                              {result ? (result.points || 0).toFixed(1) : '-'}
                            </TableCell>
                          );
                        })}
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}
                        >
                          {player.totalPoints.toFixed(1)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }}
                        >
                          {player.totalPointsWithDrop.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Vereinswertung ({clubRankings.length} Vereine)
              </Typography>

              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Rang
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Verein
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Spieler 1
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Punkte
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Spieler 2
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Punkte
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Spieler 3
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Punkte
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }}>
                        Ges
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        Spieler gesamt
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clubRankings.map((clubRanking, index) => (
                      <TableRow key={clubRanking.club}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {clubRanking.club}
                        </TableCell>
                        <TableCell>
                          {clubRanking.players[0] ? (
                            <Box>
                              {clubRanking.players[0].first_name} {clubRanking.players[0].last_name}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {clubRanking.players[0] ? clubRanking.players[0].totalPointsWithDrop.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          {clubRanking.players[1] ? (
                            <Box>
                              {clubRanking.players[1].first_name} {clubRanking.players[1].last_name}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {clubRanking.players[1] ? clubRanking.players[1].totalPointsWithDrop.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>
                          {clubRanking.players[2] ? (
                            <Box>
                              {clubRanking.players[2].first_name} {clubRanking.players[2].last_name}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {clubRanking.players[2] ? clubRanking.players[2].totalPointsWithDrop.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }}
                        >
                          {clubRanking.total.toFixed(1)}
                        </TableCell>
                        <TableCell align="right">
                          {clubRanking.playerCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default TournamentSeries;
